import "jquery-ui";
import $ from "jquery";

import React, { useRef, useMemo, useCallback, useState } from "react";
import debounce from "lodash.debounce";

import usePartyRequest, { useSaveRequest } from "../hooks/usePartyRequest";
import config from "../config";

import { 
  Error, DatePicker, Notification, 
  CompleteDialog, FileUploadDialog 
} from "../components";

import Loading, { Spinner } from "../components/loading";

import documentPane, { styles } from "../documentpane";

import { ReactComponent as DoneSvg } from "../images/done.svg";
import { ReactComponent as SetupSvg } from "../images/setup.svg";
import { ReactComponent as HighFiveSvg } from "../images/highfive.svg";
import { ReactComponent as ChecklistSvg } from "../images/checklist.svg";

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const SCHEMA_REGEX = /\/img\/forms\//g;

const privacyPolicies = {
  ACT: "https://www.reiact.com.au/privacy-policy",
  VIC: "https://formslive.com.au/static/privacy-f29c11510034669a524a0ff1b8671b3f.pdf",
  QLD: "https://formslive.com.au/static/privacy-f29c11510034669a524a0ff1b8671b3f.pdf",
  NSW: "https://nsw.reiformslive.com.au/pdf/privacy-policy.pdf",
  WA: "https://reiwa.com.au/privacy/",
  SA: "https://www.reisa.com.au/publicinfo/privacy-policy",
  FLSA: "https://sa.formslive.com.au/privacy.pdf",
};

function getPrivacyPolicy(platform) {
  var link = privacyPolicies[platform];
  if (!link) return null;

  return (
    <div className="flex items-center justify-center mt-4">
      <div className="rounded-md bg-yellow-50 p-4 mt-8 inline-flex">
        <div className="text-sm text-yellow-700 text-center">
          <p>
            By continuing, I understand and agree to the{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline" }}
              href={link}
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

const mapSchemaUrls = (schema) =>
  schema.replace(SCHEMA_REGEX, `${config.ui}/img/forms/`);

function formValuesToObject (valuesArray) {
  return (valuesArray || []).reduce((m, v) => {
    return { ...m, [v.name]: v.value };
  }, {});
}

function validateRequired (requiredFields, values) {
  if (!Array.isArray(requiredFields)) {
    requiredFields = [];
  }

  if (!values) {
    values = {}
  }

  var invalidFields = requiredFields.reduce((invalidFields, fieldName) => {    
    //leaving 'false' as valid for now, (undefined, null or emty string invalid)
    if (values[fieldName] === null || values[fieldName] === undefined || values[fieldName] === '') {
      invalidFields.push(fieldName);
    }

    //should still be valid here
    return invalidFields;
  }, []);

  return [ !invalidFields.length, invalidFields ];
}


//scroll to validation error
function scrollToFieldError () {
  var fieldErrors = $('.doc-required');
  var firstElement = fieldErrors[0];

  if (firstElement) {
    var containerEl   = $('.viewer-contain')[0];
    var containerBox  = containerEl.getBoundingClientRect();
    var fieldErrorBox = firstElement.getBoundingClientRect();;

    containerEl.scrollTo({
      top: (fieldErrorBox.y + containerEl.scrollTop) - (containerBox.height / 2),
      left: "nearest",
      behavior: "smooth",
    });
  }       
}

//util to avoid issues is dp missing setRequiredFields fn
function setRequiredFieldsSafe(inValidFields, scrollToError = true) {
  try {
    $("#viewer").documentpane('setRequiredFields', inValidFields);

    if (scrollToError && inValidFields && inValidFields.length) {
      scrollToFieldError();
    }
  } catch {}
}

const PartyRequest = ({ platform, formId, requestId }) => {
  const [notification, setNotification] = useState(null);
  const [showCompleteDialog, setCompleteOpen] = useState(false);
  const [showUploadDialog, setUploadDialogOpen] = useState(false);
  const [signingRedirectURL, setSigningRedirectURL] = useState(null);

  const hasShownUploadDialog = useRef(false);
  
  const [loading, request, error, refresh, lastUpdated, setRequest] = usePartyRequest(formId, requestId);

  const documentsRequired  = !!(request && request.party_request.documents_required);
  const documentsRequested = !!(request && request.party_request.documents_requested);
  const haSignOnComplete   = !!(request && request.party_request.sign_on_complete);

  React.useEffect(
    function autoOpenUploadDialog () {
      if (!hasShownUploadDialog.current && documentsRequired) {
        hasShownUploadDialog.current = true;
        setUploadDialogOpen(true);
      }
    }, 
    [ hasShownUploadDialog, documentsRequired ]
  );

  function onFileRemoved (removedFileId) {
    setRequest(request => {
      var files = request.form_files || [];
    
      var updated = { ...request };
      updated.form_files = [ ...files ].filter(file => file.id !== removedFileId);

      return updated;
    });

    setNotification({
      success: true,
      title: "File Removed Successfully",
      message: "The file was successfully removed.",
    });
  }

  function onFileUploaded (uploadedFile) {
    setRequest(request => {
      var files = request.form_files || [];
    
      var updated = { ...request };
      updated.form_files = [ ...files, uploadedFile ];

      return updated;
    });

    setNotification({
      success: true,
      title: "Upload Success",
      message: "The file was successfully uploaded.",
    });
  };


  //set request ref on each render so...we can just use it
  const requestRef = useRef(request);
  requestRef.current = request

  const [handleSubmit, processing] = useSaveRequest(
    formId,
    requestId,
    setNotification
  );
  
  const [showIntro, setIntroOpen] = useState(true);

  const initialFieldHighlight = useRef(false);

  React.useEffect(
    function highlightRequired() {
      var shouldHighlight = !showIntro && !initialFieldHighlight.current;
      
      if (shouldHighlight) {
        if (request && request.party_request) {
          initialFieldHighlight.current = true;
          setRequiredFieldsSafe(request.party_request.required || [], false);
        }
      }
    }, 
    [ showIntro, request ]
  );

  const closeNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const completeClickHandler = () => setCompleteOpen(true);

  const completeClose = () => setCompleteOpen(false);

  const onOpenFilesDialog = () => setUploadDialogOpen(true);

  const handleSave = useCallback(
    async (complete, opts = {}) => {
      //dirty values
      var viewer = $("#viewer");
      var dirtyValues = viewer.blur().documentpane("dirty");

      //merge with form values (previously only sending dirty fields);
      var _form = (requestRef.current && requestRef.current.form) || {};
      var formValues = formValuesToObject(_form.values || []);
      var mergedValues = Object.assign({}, formValues, dirtyValues);
      var _partyRequest = (requestRef.current && requestRef.current.party_request) || {};

      if (complete) {
        //get required fields and if we have any validate     
        var requiredFields = _partyRequest.required || [];
        
        if (requiredFields?.length) { 
          //clear required field highlight (NOTE* added to documentpane, make sure backwards compatible)
          setRequiredFieldsSafe(false);
  
          //validate required fields
          var [isValid, inValidFields] = validateRequired(requiredFields, mergedValues);
          
          if (!isValid) {
            //required fields missing, show notification + highlight and close dialog
            setRequiredFieldsSafe(inValidFields);

            setNotification({
              success: false,
              title: "A Validation Error Occurred",
              message: "Please complete all required fields highlighted in red.",
            });
            
            setCompleteOpen(false);
            return;
          }
        }
      }

      var [ success, responseData ] = await handleSubmit(mergedValues, complete);

      if (success) {
        setNotification({
          success: true,
          title: opts.title || "Details Saved",
          message: opts.message || "Successfully saved the form's data",
        });

        if (complete) {
          //NOTE* will handle redirect inside of complete dialog after timeout
          var signOnComplete = (responseData || {}).signOnComplete;
          
          if (signOnComplete) {
            setSigningRedirectURL(signOnComplete.signRedirectUrl);
          } else {
            setCompleteOpen(false);
            refresh();
          }
        }
      } else {
        var err = responseData || {};

        if (err.code === 'E_REQUIRED_FIELDS') {
          setRequiredFieldsSafe(err.fields);
          setCompleteOpen(false);
        }
      }
    },
    [handleSubmit, refresh]
  );

  const onSave = useCallback(
    (opts = {}) => {
      handleSave(false, opts);
    },
    [handleSave]
  );

  const versionSchema = useMemo(() => {
    try {
      var parsedSchema;
      
      if (request) {
        parsedSchema = JSON.parse(mapSchemaUrls(request.schema.template_version.schema))
        var userGuidePages = request.schema.template_version.user_guide_pages;

        if (userGuidePages) {
          parsedSchema.pages.splice(0, userGuidePages);
        }
      }
      
      return parsedSchema;
    } catch {
      return null;
    }
  }, [request]);

  const annexures = useMemo(() => {
    try {
      return request.schema.annexures.map((a) => ({
        ...a,
        schema: JSON.parse(mapSchemaUrls(a.schema)),
      }));
    } catch {
      return [];
    }
  }, [request]);

  const values = useMemo(() => {
    if (!request) {
      return {};
    }

    return formValuesToObject(request.form.values);
  }, [request]);

  const readonly = useMemo(() => {
    try {
      return request.schema.fields.reduce((m, field) => {
        if (~request.party_request.fields.indexOf(field.name)) {
          return m;
        }

        return [...m, field.name];
      }, []);
    } catch {
      return [];
    }
  }, [request]);

  if (loading) {
    return <Loading />;
  }

  if (error || !request) {
    return <Error message={error} />;
  }

  if (request.party_request.completed) {
    return (
      <Complete
        user={request.user}
        agency={request.agency}
        request={request.party_request}
      />
    );
  }

  if (showIntro) {
    return (
      <Intro
        haSignOnComplete={haSignOnComplete}
        user={request.user}
        agency={request.agency}
        request={request.party_request}
        template={request.schema.template}
        platform={platform}
        onNext={() => setIntroOpen(false)}
      />
    );
  }

  return (
    <React.Fragment>
      <style>{styles(platform)}</style>
      <Notification onClose={closeNotification} {...(notification || {})} />
      <CompleteDialog
        isOpen={showCompleteDialog}
        onClose={completeClose}
        onSubmit={() => handleSave(true)}
        haSignOnComplete={haSignOnComplete}
        documentsRequired={documentsRequired}
        signingRedirectURL={signingRedirectURL}
      />
      <FileUploadDialog
        isOpen={showUploadDialog}
        formId={formId} 
        request={request}
        requestId={requestId} 
        onFileRemoved={onFileRemoved}
        onFileUploaded={onFileUploaded}
        onClose={() => setUploadDialogOpen(false)}
      />
      <Form
        onSave={onSave}
        values={values}
        key={lastUpdated}
        readonly={readonly}
        platform={platform}
        form={request.form}
        formFiles={request.form_files}
        partyRequestId={request.party_request.id}
        annexures={annexures}
        processing={processing}
        schema={request.schema}
        agency={request.agency}
        versionSchema={versionSchema}
        title={request.schema.template.name}
        writeonly={request.party_request.fields}
        updated={request.party_request.updated}
        haSignOnComplete={haSignOnComplete}

        onFilesClick={onOpenFilesDialog}
        onCompleteClick={completeClickHandler}
        isFileUploadEnabled={documentsRequested}
      />
    </React.Fragment>
  );
};

const Form = (props) => {
  const activeDate = useRef(null); //TODO move date logic
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const onDateSubmit = useCallback((date) => {
    $("#viewer").documentpane(
      "setTimestamp",
      activeDate.current.data.name,
      date
    );

    setDatePickerOpen(false);
  }, []);

  const onDatePickerClose = useCallback(() => setDatePickerOpen(false), []);

  const setZoom = useCallback((scale) => {
    var viewer = $("#viewer");

    viewer.documentpane("fit", "custom");
    viewer.documentpane("scale", scale);
    viewer.documentpane("resize");
  }, []);

  const autoSize = useCallback(
    (scale) => {
      var viewerWidth = $("#viewer").innerWidth();
      var contentWidth = $(".doc-content").innerWidth();

      setZoom((viewerWidth * 0.9) / contentWidth);
    },
    [setZoom]
  );

  const zoomIn = useCallback(() => {
    setZoom($("#viewer").documentpane("scale") * 1.1);
  }, [setZoom]);

  const zoomOut = useCallback(() => {
    setZoom($("#viewer").documentpane("scale") * 0.9);
  }, [setZoom]);

  const onDateClick = useCallback((e, data) => {
    activeDate.current = {
      data: data,
      inputName: $(e.originalEvent.target).name,
    };

    setDatePickerOpen(true);
  }, []);

  const debounceSave = useMemo(() => {
    const opts = {
      title: "Auto Saved",
      message: "Form has been successfully auto saved",
    };

    return debounce(props.onSave.bind(null, opts), 4000);
  }, [props.onSave]);

  React.useLayoutEffect(() => {
    documentPane(props.platform)($);
  }, [props.platform]);

  React.useLayoutEffect(() => { 
    const viewer = $("#viewer").documentpane({
      fit: "width",
      data: props.values,
      prescale: false,
      required: false,
      logo:
        props.agency.logo &&
        ["data:image/png;base64", props.agency.logo].join(", "),
      readonly: props.readonly,
      writeonly: props.writeonly,
      finalised: props.isComplete || props.form.finalised,
      locked: props.isComplete || props.form.finalised,
      schema: props.versionSchema,
      annexures: props.annexures,
      annexureTags: props.schema.annexureTags,
      signFields: [],
      variables: {
        document_id: "",
        accredited: "",
      },
    });

    viewer.on("documentpanedate", onDateClick);
    viewer.on("documentpaneupdated", debounceSave);

    return () => {
      viewer.off("documentpanedate", onDateClick);
      viewer.off("documentpaneupdated", debounceSave);
    };
  }, [
    props.form,
    onDateClick,
    debounceSave,
    props.values,
    props.schema,
    props.agency,
    props.writeonly,
    props.platform,
    props.readonly,
    props.annexures,
    props.isComplete,
    props.versionSchema,
  ]);

  React.useLayoutEffect(() => {
    autoSize();
  }, [autoSize]);

  React.useLayoutEffect(
    function renderAttachments() {
      var queue = {};
      var current = 0;

      const queueFiles = (files) => {
        queue = files;
        current = 0;
        load();
      };

      const renderPage = (pdf, pageNumber, fileId, file) => {
        pdf.getPage(pageNumber).then(function (page) {
      
          /* 
              NOTE use same page scale as print when we have attachment page dimensions
              since page container will be at this scale to match print 
            */

          const DOCUMENT_PRINT_SCALE = 3.53;
          var desiredViewportScale = DOCUMENT_PRINT_SCALE;

          /*
              NOTE* if we don't have page dimensions then we're rendering inside A4
              so we need to calculate how much to scale by while maintaing aspect ratio
            */

          const pageDimensions = file?.page_dimensions?.find((d) => d.page === pageNumber);
          
          var $canvas = $(`#file_${fileId}_${pageNumber}`);
          var container = $canvas.closest('.doc-content');

          if (!pageDimensions) {
            /*
              NOTE* previously this logic just using viewport width and height for orientation (when page dimensions unavailable)
              this would cause landscape files to be cut off in many cases instead of scaling within container bounds
              have updated to use page dimensions for orientation instead of canvas viewport
            */
            var viewport = page.getViewport({ scale: 1 });
            var orientation = container.width > container.height ? "landscape" : "portrait";
            var pageWidth = orientation === "landscape" ? 2970 : 2100;
            desiredViewportScale = pageWidth / viewport.width;
          }

          //NOTE* we should now have correct viewport scale for attachment page
          var scaledViewport = page.getViewport({ scale: desiredViewportScale });

          var canvas = $canvas[0];
          var context = canvas.getContext("2d");
          canvas.height = scaledViewport.height;
          canvas.width = scaledViewport.width;

          var renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
          };

          var renderTask = page.render(renderContext);

          renderTask.promise.then(function () {
            if (pageNumber + 1 <= pdf.numPages) {
              renderPage(pdf, pageNumber + 1, fileId, file);
            } else {
              current++;
              load();
            }
          });
        });
      };

      const load = () => {
        var file = queue[current];

        if (!file) return false;

        var url = `${config.api}/requests/${props.form.id}/${props.partyRequestId}/files/${file.id}`;

        try {
          var _loadingTask = pdfjsLib.getDocument(url);

          _loadingTask.promise
            .then(function (pdf) { renderPage(pdf, 1, file.id, file); })
            .catch(err => { 
              //error loading PDF (eg loading started and party upload removed) 
            });

        } catch (err) {
          //getDocument can throw, adding catch here for now
        }
      };

      var items = [];
      var formFiles = props.formFiles || [];

      var getFields = function (page) {
        return fields.filter(function (f) {
          return f.page === page;
        });
      };

      function mapDimensions(width, height) {
        return {
          width: width,
          height: height,
          maxWidth: width,
          maxHeight: height
        };
      }

      for (var j = 0; j < formFiles.length; j++) {
        var file      = formFiles[j];
        var fileIndex = file.id;
        var numPages  = file.pages;
        var fields    = file.fields || [];

        for (var i = 0; i < numPages; i++) {
          var page = i+1;          

          //find page dimensions in array
          var foundDimensions = null;
          for (var _dimensions of (file.page_dimensions || [])) {
            if (_dimensions.page === page) {
              foundDimensions = _dimensions;
              break;
            }
          }

          //map page dimensions using found values or default to A4 portrat
          var pageDimensions = null;
          if (foundDimensions) {
            var PRINT_SCALE_FACTOR = 3.53;
    
            pageDimensions = mapDimensions(foundDimensions.width * PRINT_SCALE_FACTOR, foundDimensions.height * PRINT_SCALE_FACTOR);
          } else {
            var PAGE_WIDTH = 2100;
            var PAGE_HEIGHT = 2970;

            pageDimensions = mapDimensions(PAGE_WIDTH, PAGE_HEIGHT);
          }

          items.push({
            index: formFiles[j].index,
            pages: [
              {
                form: getFields(i + 1),
                content: [],
                ...pageDimensions,
                background: [
                  {
                    type: "text",
                    left: 0,
                    top: 0,
                    html: `<canvas id="file_${fileIndex}_${i + 1}"></canvas>`,
                  },
                ],
              },
            ],
          });
        }
      }

      queueFiles(formFiles);
      $("#viewer").documentpane("updateAttachments", items);
      autoSize();
    },
    [props.form.id, props.formFiles, props.partyRequestId, autoSize]
  );

  return (
    <div
      style={{ height: "calc(100vh - 64px)" }}
      className="bg-gray-50 flex flex-col overflow-hidden"
    >
      <div
        style={{ zIndex: 10 }}
        className={
          "flex items-center justify-between py-2.5 px-6 shadow border-b border-gray-200 bg-white"
        }
      >
        <div className="flex flex-row">
          <button
            className="mr-1 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={zoomOut}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
              />
            </svg>
          </button>
          <button
            className="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={zoomIn}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
              />
            </svg>
          </button>
        </div>
        <h1 className="font-semibold text-md text-gray-800 leading-5 text-center px-4 truncate text-ellipsis overflow-hidden">
          {props.title}
        </h1>

        <div className="flex flex-row">
          {
            props.isFileUploadEnabled &&
            <button
              disabled={props.processing}
              className={
                "relative inline-flex items-center px-2.5 py-1.5 leading-5 border border-transparent text-sm font-medium rounded shadow-sm text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 " +
                (props.processing
                  ? "pl-8 bg-gray-400 pointer-events-none"
                  : "bg-gray-800")
              }
              onClick={props.onFilesClick}
            >
              {props.processing && <Spinner />} <span className="hidden md:inline">Upload&nbsp;</span> Files
            </button>
          }
          <button
            disabled={props.processing}
            className={
              "relative ml-2 inline-flex items-center px-2.5 py-1.5 leading-5 border border-transparent text-sm font-medium rounded shadow-sm text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 " +
              (props.processing
                ? "pl-8 bg-gray-400 pointer-events-none"
                : "bg-gray-800")
            }
            onClick={props.onSave}
          >
            {props.processing && <Spinner />} Save
          </button>
          
          <button
            disabled={props.processing}
            className={
              "relative ml-2 inline-flex items-center px-2.5 py-1.5 leading-5 border border-transparent text-sm font-medium rounded shadow-sm text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 " +
              (props.processing
                ? "pl-8 bg-gray-400 pointer-events-none"
                : "bg-green-500")
            }
            onClick={props.onCompleteClick}
          >
            {props.processing && <Spinner />} {props.haSignOnComplete ? 'Continue to sign' : 'Complete'}
          </button>
        </div>
      </div>
      
      <DatePicker
        onSubmit={onDateSubmit}
        isOpen={datePickerOpen}
        onClose={onDatePickerClose}
        defaultDate={
          activeDate.current &&
          activeDate.current.data &&
          activeDate.current.data.initialValue
        }
      />
      <div className="viewer-contain relative overflow-auto">
        <div id="viewer" className="relative pb-8 origin-top" />
      </div>
    </div>
  );
};

const Complete = ({ agency, user, request }) => {
  return (
    <div className="mt-10 mx-auto max-w-screen-xl px-8 mt-8 xl:mt-16">
      <div className="text-center">
        <h2 className="text-3xl mb-8 font-semibold leading-6 text-gray-900">
          Thanks {request.name}, you're all set!
        </h2>
      </div>
      <div>
        <HighFiveSvg
          className="h-64 w-64 sm:w-auto mx-auto my-12 xl:my-24 text-secondary"
          alt="Complete"
        />
      </div>
      <p className="text-xl text-center text-gray-500 my-8">
        If you have any questions, please reach out to us.
      </p>

      <div className="mx-auto max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-8 my-8 lg:my-16">
        <div className="flex flex-col justify-between items-baseline">
          <div>
            <p className="text-2xl font-semibold text-primary leading-12">
              {agency.name}
            </p>
            <p className="text-xl text-gray-500">{agency.phone}</p>
            <p className="text-lg text-gray-500">{agency.email}</p>
          </div>
          <a
            type="button"
            className="w-full sm:w-auto inline-flex justify-center text-primary-btn-text bg-primary-btn-background rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 px-12 py-3 text-md font-medium mt-2"
            href={"mailto:" + agency.email}
          >
            Email Agency
          </a>
        </div>

        <div className="flex flex-col justify-between items-baseline">
          <div>
            <p className="text-2xl font-semibold text-primary leading-12">
              {user.given_name} {user.surname}
            </p>
            <p className="text-xl text-gray-500">{user.mobile}</p>
            <p className="text-lg text-gray-500">{user.email}</p>
          </div>
          <a
            type="button"
            className="w-full sm:w-auto inline-flex justify-center text-primary-btn-text bg-primary-btn-background rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 px-12 py-3 text-md font-medium mt-2"
            href={"mailto:" + user.email}
          >
            Email Agent
          </a>
        </div>
      </div>
    </div>
  );
};

const Intro = ({ request, user, template, agency, platform, haSignOnComplete, onNext }) => {
  var privacyPolicy = getPrivacyPolicy(platform);

  return (
    <div className="mx-auto max-w-screen-xl px-8 mt-8 lg:mt-16">
      <div className="w-full overflow-hidden text-left align-middle text-center">
        <h3 className="text-3xl mb-8 font-semibold leading-6 text-gray-900">
          Hello, {request.name}!
        </h3>
        <div className="mt-4">
          <p className="text-gray-800 text-lg leading-6">
            <span className="font-semibold">{user.given_name} </span>here from{" "}
            <span className="font-semibold">{agency.name}</span>.
          </p>
          <p className="text-gray-800 text-lg leading-8">
            We need your help completing the {template.name}.
          </p>

          <h3 className="text-xl font-semibold leading-12 text-gray-900 mt-8 2xl:mt-16">
            We've made the process super simple for you!
          </h3>
          <p className="text-gray-500 text-lg leading-8">
            Here are a few pointers to help make it as painless as possible
          </p>
        </div>
      </div>

      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-8 mt-2 lg:mt-4">
        <div className="text-center">
          <SetupSvg
            className="p-4 py-8 w-full h-64 text-secondary"
            alt="Form Fields"
          />
          <p className="px-6 mt-4">
            All fields that need to be filled out are highlighted for you
          </p>
        </div>
        <div className="text-center">
          <ChecklistSvg

            className="p-4 py-12 h-64 w-full text-secondary"
            alt="Save Progress"
          />
          <p className="px-6 mt-4">
            Save your progress by clicking the{" "}
            <span className="font-semibold">Save</span> button & return at any
            point
          </p>
        </div>
        <div className="text-center">
          <DoneSvg
            className="p-4 w-full h-64 text-secondary"
            alt="Complete Document"
          />
          <p className="px-6 mt-4">
            Submit the completed form by clicking the{" "}
            <span className="font-semibold">{haSignOnComplete ? 'Continue to sign' : 'Complete'}</span> button
            {!!haSignOnComplete && <span> and then proceed to sign document.</span>}
          </p>
        </div>
      </div>

      {
        !haSignOnComplete &&
        <div className="flex items-center justify-center mt-4">
          <div className="rounded-md bg-yellow-50 p-4 mt-8 inline-flex">
            <div className="text-sm text-yellow-700 text-center">
              <p>
                <span className="font-semibold">Note</span> You are not required
                to sign the form at this stage. Once completed, the agent will
                review the information and be in touch.
              </p>
            </div>
          </div>
        </div>
      }

      {privacyPolicy}

      <div className="flex justify-center mt-8 pb-8">
        <button
          type="button"
          className="w-full md:w-auto inline-flex justify-center text-primary-btn-text bg-primary-btn-background border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500  px-6 py-3 text-md font-medium xl:px-16 xl:py-4 xl:text-xl"
          onClick={onNext}
        >
          Get Started
        </button>
      </div>
    </div>
  );
};

export default PartyRequest;
